import { Usage } from "@hydrologiq/schemas/1.0.0/installationPage"
import Energy from "../../../figures/Energy"
import { EnergyPeriod, EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import {
  IANATimeZone,
  endOfDay,
  formatYYYYMMDD,
  nowDateTime,
  previousDay,
  startOfDay,
  toTimestamp,
} from "@hydrologiq/common/helpers/date"
import { useQuery } from "@apollo/client"
import { DateTime, TimeZone } from "timezonecomplete"
import { WeightDifferenceComparisonDownSampledDocument } from "@hydrologiq/gql/graphql"

export interface UsageFigureProps {
  title: string
  usage: Usage
  installationId: string
  installationEndDate: DateTime
  directComparison?: boolean
}

function WeightUsageFigure({ title, usage, installationId, installationEndDate, directComparison }: UsageFigureProps) {
  const dateNow = nowDateTime()

  const endOfInstallation = dateNow.unixUtcMillis() > installationEndDate.unixUtcMillis()
  const endDate = endOfInstallation ? installationEndDate : dateNow
  const prevDate = previousDay(endDate, endOfInstallation)
  const dayBefore = previousDay(prevDate, endOfInstallation)

  const { loading, error, data } = useQuery(WeightDifferenceComparisonDownSampledDocument, {
    variables: {
      id: installationId,
      start: toTimestamp(startOfDay(prevDate), true),
      stop: toTimestamp(endOfDay(prevDate), true),
      compStart: toTimestamp(startOfDay(dayBefore), true),
      compStop: toTimestamp(endOfDay(dayBefore), true),
      location: IANATimeZone(),
    },
  })

  const noComparison = data && data.comparison && data.comparison.length == 1 && !data.comparison[0]?.value

  // If the previous day time is today then we set true
  const today =
    !noComparison ||
    (data?.value?.[0]?.time &&
      formatYYYYMMDD(new DateTime(data.value[0].time)) === formatYYYYMMDD(endDate.convert(TimeZone.utc())))

  return (
    <Energy
      heading={title}
      desiredUnit={usage.desiredUnit as EnergyUnit}
      quantity={data && data.value && data.value.length == 1 && data.value[0] ? (data.value[0].value as number) : 0}
      period={today ? "today" : (usage.period as EnergyPeriod)}
      previousQuantity={
        data && data.comparison && data.comparison.length == 1 && data.comparison[0]
          ? (data.comparison[0].value as number) ?? 0
          : 0
      }
      dp={2}
      sourceUnit={usage.sourceUnit && (usage.sourceUnit as EnergyUnit)}
      loading={loading}
      error={error ? error.message : undefined}
      comparisonDirect={directComparison}
    />
  )
}

export default WeightUsageFigure
